import { useState, type DragEvent } from 'react';

import { IconButton, NumberField, SelectField } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { docVariables, ParamField } from './ParamField.js';
import { SchemaFields, withField } from './SchemaFields.js';
import { isNum, isObj, paramToNumber, r4 } from './param-values.js';
import {
  DEFAULT_EFFECTS,
  EFFECT_FIELDS,
  EFFECT_GROUPS,
  EFFECT_TYPES,
  EQ_BAND_TYPES,
  type EffectType,
} from './sound-catalog.js';
import './sound-editors.css';

type Fx = Record<string, unknown>;

/** Track and return-bus names a compressor can sidechain from. */
export function sidechainSources(doc: unknown): string[] {
  if (!isObj(doc)) return [];
  const names: string[] = [];
  if (Array.isArray(doc.tracks)) for (const t of doc.tracks) if (isObj(t) && typeof t.name === 'string') names.push(t.name);
  if (Array.isArray(doc.layers)) for (const l of doc.layers) if (isObj(l) && typeof l.name === 'string') names.push(l.name);
  if (isObj(doc.master) && isObj(doc.master.returns)) names.push(...Object.keys(doc.master.returns));
  return [...new Set(names)];
}

/** Moves item `from` to index `to` (returns a copy). */
export function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  const next = [...list];
  const [item] = next.splice(from, 1);
  if (item === undefined) return next;
  next.splice(Math.max(0, Math.min(next.length, to)), 0, item);
  return next;
}

/* ------------------------------------------------------------------ */
/* EQ                                                                  */

const RATE = 48000;

/** Magnitude in dB of one RBJ biquad band at `f` Hz. */
export function bandResponseDb(type: string, f0: number, q: number, gainDb: number, f: number): number {
  const A = Math.pow(10, gainDb / 40);
  const w0 = (2 * Math.PI * Math.min(f0, RATE * 0.49)) / RATE;
  const cos = Math.cos(w0);
  const alpha = Math.sin(w0) / (2 * Math.max(0.05, q));
  let b0: number, b1: number, b2: number, a0: number, a1: number, a2: number;
  switch (type) {
    case 'lowpass':
      [b0, b1, b2, a0, a1, a2] = [(1 - cos) / 2, 1 - cos, (1 - cos) / 2, 1 + alpha, -2 * cos, 1 - alpha];
      break;
    case 'highpass':
      [b0, b1, b2, a0, a1, a2] = [(1 + cos) / 2, -(1 + cos), (1 + cos) / 2, 1 + alpha, -2 * cos, 1 - alpha];
      break;
    case 'bandpass':
      [b0, b1, b2, a0, a1, a2] = [alpha, 0, -alpha, 1 + alpha, -2 * cos, 1 - alpha];
      break;
    case 'notch':
      [b0, b1, b2, a0, a1, a2] = [1, -2 * cos, 1, 1 + alpha, -2 * cos, 1 - alpha];
      break;
    case 'lowshelf': {
      const s = 2 * Math.sqrt(A) * alpha;
      [b0, b1, b2] = [A * (A + 1 - (A - 1) * cos + s), 2 * A * (A - 1 - (A + 1) * cos), A * (A + 1 - (A - 1) * cos - s)];
      [a0, a1, a2] = [A + 1 + (A - 1) * cos + s, -2 * (A - 1 + (A + 1) * cos), A + 1 + (A - 1) * cos - s];
      break;
    }
    case 'highshelf': {
      const s = 2 * Math.sqrt(A) * alpha;
      [b0, b1, b2] = [A * (A + 1 + (A - 1) * cos + s), -2 * A * (A - 1 + (A + 1) * cos), A * (A + 1 + (A - 1) * cos - s)];
      [a0, a1, a2] = [A + 1 - (A - 1) * cos + s, 2 * (A - 1 - (A + 1) * cos), A + 1 - (A - 1) * cos - s];
      break;
    }
    default:
      [b0, b1, b2, a0, a1, a2] = [1 + alpha * A, -2 * cos, 1 - alpha * A, 1 + alpha / A, -2 * cos, 1 - alpha / A];
  }
  const w = (2 * Math.PI * f) / RATE;
  const re = (c0: number, c1: number, c2: number) => c0 + c1 * Math.cos(w) + c2 * Math.cos(2 * w);
  const im = (c1: number, c2: number) => -(c1 * Math.sin(w) + c2 * Math.sin(2 * w));
  const num = Math.hypot(re(b0, b1, b2), im(b1, b2));
  const den = Math.hypot(re(a0, a1, a2), im(a1, a2));
  return 20 * Math.log10(Math.max(1e-9, num / den));
}

function EqCurve({ bands }: { bands: Fx[] }) {
  const W = 280;
  const H = 70;
  const range = 18;
  const pts: string[] = [];
  for (let i = 0; i <= 120; i++) {
    const f = 20 * Math.pow(1000, i / 120);
    let db = 0;
    for (const b of bands) {
      db += bandResponseDb(
        typeof b.type === 'string' ? b.type : 'peak',
        paramToNumber(b.freq, {}, 1000),
        paramToNumber(b.q, {}, 0.707),
        paramToNumber(b.gain, {}, 0),
        f,
      );
    }
    const y = H / 2 - (Math.max(-range, Math.min(range, db)) / range) * (H / 2 - 4);
    pts.push(`${r4((i / 120) * W)},${r4(y)}`);
  }
  return (
    <svg className="snd-graph is-static" viewBox={`0 0 ${W} ${H}`} aria-label="EQ response (20 Hz – 20 kHz, ±18 dB)">
      {[100, 1000, 10000].map((f) => {
        const x = (Math.log10(f / 20) / 3) * W;
        return <line key={f} className="snd-graph-grid" x1={x} x2={x} y1={0} y2={H} />;
      })}
      <line className="snd-graph-zero" x1={0} x2={W} y1={H / 2} y2={H / 2} />
      <polyline className="snd-graph-line" points={pts.join(' ')} />
      <text className="snd-graph-label" x={W - 3} y={10} textAnchor="end">
        +18 dB
      </text>
    </svg>
  );
}

function EqBands({ value, onChange, vars }: { value: unknown; onChange: (v: unknown) => void; vars: Record<string, number> }) {
  const bands: Fx[] = Array.isArray(value) ? value.map((b) => (isObj(b) ? b : {})) : [];
  const set = (i: number, key: string, v: unknown) => onChange(bands.map((b, j) => (j === i ? withField(b, key, v) : b)));
  return (
    <div className="snd-list">
      <EqCurve bands={bands} />
      {bands.map((b, i) => (
        <div key={i} className="snd-subcard">
          <div className="snd-row">
            <SelectField value={typeof b.type === 'string' ? b.type : 'peak'} options={EQ_BAND_TYPES} onChange={(t) => set(i, 'type', t)} />
            <IconButton icon="x" title="Remove band" size={12} onClick={() => onChange(bands.filter((_, j) => j !== i))} />
          </div>
          <div className="snd-grid2">
            <span className="snd-mini-label">Freq</span>
            <ParamField value={b.freq} step={10} min={10} max={24000} placeholder={1000} vars={vars} onChange={(v) => set(i, 'freq', v)} />
            <span className="snd-mini-label">Q</span>
            <ParamField value={b.q} step={0.05} min={0.05} placeholder={0.707} vars={vars} onChange={(v) => set(i, 'q', v)} />
            {['peak', 'lowshelf', 'highshelf'].includes(String(b.type ?? 'peak')) && (
              <>
                <span className="snd-mini-label">Gain</span>
                <ParamField value={b.gain} step={0.5} placeholder={0} vars={vars} onChange={(v) => set(i, 'gain', v)} />
              </>
            )}
          </div>
        </div>
      ))}
      <div className="snd-row">
        <IconButton icon="plus" title="Add band" size={13} onClick={() => onChange([...bands, { type: 'peak', freq: 1000, q: 1, gain: 0 }])} />
        <span className="snd-hint">{`${bands.length} bands`}</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* One effect                                                          */

function EffectCard({
  fx,
  index,
  onChange,
  onRemove,
  onMove,
  count,
  doc,
  dragging,
  onDragStart,
  onDropAt,
}: {
  fx: Fx;
  index: number;
  count: number;
  onChange: (fx: Fx) => void;
  onRemove: () => void;
  onMove: (to: number) => void;
  doc: unknown;
  dragging: boolean;
  onDragStart: () => void;
  onDropAt: () => void;
}) {
  const [open, setOpen] = useState(true);
  const [over, setOver] = useState(false);
  const type = typeof fx.type === 'string' ? fx.type : '';
  const fields = EFFECT_FIELDS[type as EffectType];
  const vars = docVariables(doc);
  const bypass = fx.bypass === true;

  return (
    <div
      className={`snd-fx${bypass ? ' is-bypassed' : ''}${dragging ? ' is-dragging' : ''}${over ? ' is-drop' : ''}`}
      onDragOver={(e: DragEvent) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        onDropAt();
      }}
    >
      <div className="snd-fx-head">
        <span
          className="snd-fx-grip"
          draggable
          title="Drag to reorder"
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', String(index));
            onDragStart();
          }}
        >
          ⋮⋮
        </span>
        <button type="button" className="snd-fx-title" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          <Icon name={open ? 'chevronDown' : 'chevronRight'} size={12} />
          <span>{type || 'effect'}</span>
          {!fields && <span className="snd-chip is-warn">unknown type</span>}
        </button>
        <span className="snd-fx-mix" title="Wet/dry mix (0..1)">
          {isNum(fx.mix) || fx.mix === undefined ? (
            <NumberField label="Mix" value={isNum(fx.mix) ? fx.mix : 1} step={0.05} min={0} max={1} onChange={(v) => onChange(withField(fx, 'mix', v === 1 ? undefined : v))} />
          ) : (
            <span className="snd-chip">mix ~</span>
          )}
        </span>
        <IconButton icon={bypass ? 'eyeOff' : 'eye'} title={bypass ? 'Enable (bypassed)' : 'Bypass'} size={13} active={bypass} onClick={() => onChange(withField(fx, 'bypass', bypass ? undefined : true))} />
        <IconButton icon="chevronUp" title="Move up" size={12} disabled={index === 0} onClick={() => onMove(index - 1)} />
        <IconButton icon="chevronDown" title="Move down" size={12} disabled={index >= count - 1} onClick={() => onMove(index + 1)} />
        <IconButton icon="trash" title="Remove effect" size={13} onClick={onRemove} />
      </div>
      {open && (
        <div className="snd-fx-body">
          <SchemaFields
            obj={fx}
            fields={fields ?? []}
            onChange={onChange}
            vars={vars}
            skip={['type', 'mix', 'bypass']}
            renderCustom={(f) => {
              if (f.key === 'bands') return <EqBands value={fx.bands} vars={vars} onChange={(v) => onChange(withField(fx, 'bands', v))} />;
              if (f.key === 'sidechain') {
                const sources = sidechainSources(doc);
                return (
                  <SelectField
                    value={typeof fx.sidechain === 'string' ? fx.sidechain : undefined}
                    placeholder="None (self)"
                    options={[{ value: '', label: 'None (self)' }, ...sources.map((s) => ({ value: s, label: s }))]}
                    onChange={(v) => onChange(withField(fx, 'sidechain', v || undefined))}
                  />
                );
              }
              return undefined;
            }}
          />
          {!isNum(fx.mix) && fx.mix !== undefined && (
            <div className="prop-row">
              <span className="prop-label">Mix</span>
              <div className="prop-control">
                <ParamField value={fx.mix} step={0.05} min={0} max={1} vars={vars} onChange={(v) => onChange(withField(fx, 'mix', v))} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Effect chain editor for any `effects` array (layer, track, bus, master,
 * instrument, voice): add / remove / reorder (drag or arrows) / bypass,
 * typed fields per effect type, wet/dry `mix`.
 */
export function EffectsEditor({
  value,
  onChange,
  doc,
}: {
  value: unknown;
  onChange: (value: unknown) => void;
  doc?: unknown;
}) {
  const list: Fx[] = Array.isArray(value) ? value.map((f) => (isObj(f) ? f : {})) : [];
  const [drag, setDrag] = useState<number | null>(null);
  const commit = (next: Fx[]) => onChange(next.length ? next : undefined);
  const options = EFFECT_GROUPS.flatMap((g) => g.types.map((t) => ({ value: t, label: `${g.label} · ${t}` })));

  return (
    <div className="snd-fxchain">
      {list.length === 0 && <span className="snd-hint">No effects. Effects apply in order, top to bottom.</span>}
      {list.map((fx, i) => (
        <EffectCard
          key={i}
          fx={fx}
          index={i}
          count={list.length}
          doc={doc}
          dragging={drag === i}
          onDragStart={() => setDrag(i)}
          onDropAt={() => {
            if (drag !== null && drag !== i) commit(moveItem(list, drag, i));
            setDrag(null);
          }}
          onChange={(next) => commit(list.map((f, j) => (j === i ? next : f)))}
          onRemove={() => commit(list.filter((_, j) => j !== i))}
          onMove={(to) => commit(moveItem(list, i, to))}
        />
      ))}
      <div className="snd-row">
        <SelectField
          value={undefined}
          placeholder="Add effect…"
          options={options.length === EFFECT_TYPES.length ? options : EFFECT_TYPES}
          onChange={(t) => {
            if (!t) return;
            commit([...list, structuredClone(DEFAULT_EFFECTS[t as EffectType] ?? { type: t })]);
          }}
        />
      </div>
    </div>
  );
}
